import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type {
    PrhHolidayHomeDTO,
    PrhHouseDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';
import { pick } from 'lodash';
import type { HouseAttributeConfig } from '../../../../hooks/usePrhHouseOPC';
import type { OPCAttribute } from '../../../../recoil/types/opcTypes';
import ViewHippoInformationModal from '../../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';

type AttributeNameMap = Record<
    keyof Pick<Attributes, 'primaryHeatingType' | 'numberOfToilets' | 'fireplace'>,
    OPCAttribute
>;

const attributeNameToOpcLabelMap: AttributeNameMap = {
    primaryHeatingType: 'prhPrimaryHeating',
    numberOfToilets: 'prhNumOfToilets',
    fireplace: 'prhFirePlace'
};

export type HouseAttributes = Pick<
    PrhHouseDTO,
    | 'basementArea'
    | 'livingArea'
    | 'listedWorthPreservingType'
    | 'waterProtectionType'
    | 'rental'
    | 'garageOuthouseArea'
    | 'commercialActivitiesInTheBuilding'
    | 'roofType'
    | 'exteriorWallType'
    | 'constructionYear'
    | 'primaryHeatingType'
    | 'developedArea'
    | 'closedDownFarm'
    | 'numberOfToilets'
    | 'numberOfFloors'
    | 'closedDownCommercial'
    | 'roofOver20'
    | 'roofOver30'
    | 'allDoorsAndWindowsOver30AndMadeOfWood'
    | 'firstRiskSum'
    | 'commercialArea'
    | 'buildingUse'
    | 'commActivityType'
    | 'basement'
>;

type HolidayHomeAttributes = Pick<PrhHolidayHomeDTO, 'fireplace'>;
type Attributes = HouseAttributes & HolidayHomeAttributes;

const editableAttributes: Array<keyof Attributes> = [
    'primaryHeatingType',
    'numberOfToilets',
    'fireplace'
];

const callMeAttributes: Array<keyof Attributes> = [
    'basement',
    'buildingUse',
    'constructionYear',
    'livingArea',
    'developedArea',
    'garageOuthouseArea',
    'numberOfFloors',
    'basementArea',
    'exteriorWallType',
    'roofType',
    'waterProtectionType',
    'listedWorthPreservingType',
    'closedDownFarm',
    'rental',
    'commercialArea',
    'firstRiskSum',
    'commercialActivitiesInTheBuilding',
    'commActivityType'
];

const allowedAttributesLabelMap: Record<keyof Attributes, string> = {
    basement: 'DSS-EDIT-ATTRIBUTES-BASEMENT-LABEL',
    basementArea: 'DSS-EDIT-ATTRIBUTES-BASEMENT-AREA-LABEL',
    livingArea: 'DSS-EDIT-ATTRIBUTES-LIVING-AREA-LABEL',
    listedWorthPreservingType: 'DSS-EDIT-ATTRIBUTES-LISTED-WORTH-PRESERVING-LABEL',
    waterProtectionType: 'DSS-EDIT-ATTRIBUTES-WATER-PROTECTION-LABEL',
    rental: 'DSS-EDIT-ATTRIBUTES-RENTAL-LABEL',
    garageOuthouseArea: 'DSS-EDIT-ATTRIBUTES-GARAGE-OUTHOUSE-AREA-LABEL',
    commercialActivitiesInTheBuilding: 'DSS-EDIT-ATTRIBUTES-COMMERCIAL-ACTIVITY-IN-BUILDING-LABEL',
    roofType: 'DSS-EDIT-ATTRIBUTES-ROOF-TYPE-LABEL',
    exteriorWallType: 'DSS-EDIT-ATTRIBUTES-WALL-TYPE-LABEL',
    constructionYear: 'DSS-EDIT-ATTRIBUTES-CONSTRUCTION-YEAR-LABEL',
    primaryHeatingType: 'DSS-EDIT-ATTRIBUTES-PRIMARY-HEATING-LABEL',
    developedArea: 'DSS-EDIT-ATTRIBUTES-DEVELOPED-AREA-LABEL',
    closedDownFarm: 'DSS-EDIT-ATTRIBUTES-CLOSED-DOWN-FARM-LABEL',
    numberOfToilets: 'DSS-EDIT-ATTRIBUTES-TOILET-LABEL',
    numberOfFloors: 'DSS-EDIT-ATTRIBUTES-FLOORS-LABEL',
    closedDownCommercial: 'DSS-EDIT-ATTRIBUTES-CLOSED-DOWN-COMMERCIAL-LABEL',
    roofOver20: 'DSS-EDIT-ATTRIBUTES-ROOF-20-LABEL',
    roofOver30: 'DSS-EDIT-ATTRIBUTES-ROOF-30-LABEL',
    allDoorsAndWindowsOver30AndMadeOfWood: 'DSS-EDIT-ATTRIBUTES-DOORS-WINDOWS-WOOD-30-LABEL',
    fireplace: 'DSS-EDIT-ATTRIBUTES-WOOD-STOVE—FIREPLACE-LABEL',
    firstRiskSum: 'DSS-EDIT-ATTRIBUTES-FIRST-RISK-SUM-LABEL',
    commercialArea: 'DSS-EDIT-ATTRIBUTES-COMMERCIAL-AREAROOF-TYPE-LABEL',
    buildingUse: 'DSS-EDIT-ATTRIBUTES-BUILDING-USE-LABEL',
    commActivityType: 'DSS-EDIT-ATTRIBUTES-COMMERCIAL-ACTIVITY-LABEL'
};

interface AttributeModalData {
    title: string;
    description: string;
}

const mapAttributeNameToModalInformation: Record<string, AttributeModalData> = {
    livingArea: {
        title: 'DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE1',
        description: 'DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-BODY1'
    },
    developedArea: {
        title: 'DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE3',
        description: 'DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-BODY3'
    },
    closedDownCommercial: {
        title: 'DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-TITLE',
        description: 'DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-BODY'
    },
    closedDownFarm: {
        title: 'DSS-COVERABLE-INFORMATION-CLOSED-FARM-TITLE',
        description: 'DSS-COVERABLE-INFORMATION-CLOSED-FARM-BODY'
    },
    listedWorthPreservingType: {
        title: 'DSS-COVERABLE-INFORMATION-PRESERVED-TITLE',
        description: 'DSS-COVERABLE-INFORMATION-PRESERVED-BODY'
    },
    waterProtectionType: {
        title: 'DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-TITLE',
        description: 'DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-BODY'
    }
};
const hasInfoModal = (attributeName: string): boolean => {
    return Object.keys(mapAttributeNameToModalInformation).includes(attributeName);
};
const compareAttributes = (a: HouseAttributeConfig, b: HouseAttributeConfig): number => {
    if (
        (a.editable && !b.editable) ||
        (a.willTriggerCallMeModal && !b.editable && !b.willTriggerCallMeModal)
    )
        return -1;
    return 0;
};
const privateHouseAttributesConfig = (
    attributes: CoverableDTO,
    getHippoText: (resourceKey: string) => string
): HouseAttributeConfig[] => {
    const allowedAttributes = Object.keys(allowedAttributesLabelMap) as Array<keyof Attributes>;
    const filteredAttributes = Object.keys(pick(attributes, allowedAttributes)) as Array<
        keyof Attributes
    >;
    const attributeConfigArray = filteredAttributes.map((attribute) => {
        const isEditable = editableAttributes.includes(attribute);
        return {
            name: attribute,
            value: (attributes as PrhHouseDTO)[attribute as keyof PrhHouseDTO] as string,
            editable: isEditable,
            willTriggerCallMeModal: callMeAttributes.includes(attribute),
            opcAttribute: attributeNameToOpcLabelMap[attribute as keyof AttributeNameMap],
            hippoLabel: allowedAttributesLabelMap[attribute],
            ...(hasInfoModal(attribute) && {
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText(mapAttributeNameToModalInformation[attribute].title),
                        description: getHippoText(
                            mapAttributeNameToModalInformation[attribute].description
                        )
                    }
                }
            })
        };
    });

    return [...attributeConfigArray].sort(compareAttributes);
};
export default privateHouseAttributesConfig;
